import {
  selectRouterPathname,
  selectRouterQueryParameters,
} from '@noths/polaris-client-next-redux';
import { handleFetchResponse, requestWithTimeout } from '@noths/polaris-client-utils';
import { Product } from '@noths/polaris-dev-ts-types';
import { createAsyncThunk, isRejected, unwrapResult } from '@reduxjs/toolkit';
import { replace } from 'connected-next-router';
import fetch from 'isomorphic-fetch';
import getConfig from 'next/config';
import qs, { ParsedUrlQueryInput } from 'querystring';

import { PRODUCT_CONTAINER_WRAPPER } from 'src/constants/elementIds';
import { selectPageType } from 'src/redux/navigation';
import { BrowseServiceProductsData } from 'src/services/browse-data-api/types';
import { pageHistory } from 'src/services/pageHistory';
import { NextConfig, PageType } from 'src/types';
import { BrowseProvider } from '../../constants/featureFlags';
import {
  getAllProductsListingsData,
  getCategoryListingsData,
  getPartnerListingsData,
  getSearchListingsData,
} from '../../services/browse-data-api/getListingsData';
import { transformBrowseServiceProductsToReduxActionPayload } from '../../services/browse-data-api/transform';
import { ReduxApplicationState } from '../combinedReducer';
import { addLoadingProducts } from './actions';
import {
  selectBrowseProviderFeatureFlag,
  selectCurrentPage,
  selectPartnerShortcode,
  selectRequestOptionsAsQueryObject,
} from './selectors';
import { ProductsState, RequestProductsActionPayload, TriggerName } from './types';

const { publicRuntimeConfig } = getConfig() as NextConfig;

export interface RequestProductsOptions {
  triggerName?: TriggerName;
  query: ParsedUrlQueryInput;
  cookie?: string;
  replaceProducts?: boolean;
  isHealthcheckRequest?: boolean;
}

interface ProductsAPIHandledRejection {
  isServerTimeout?: boolean;
  isClientAbort?: boolean;
}

export enum UpdateFiltersActionType {
  SORT = 'SORT',
  FILTER = 'FILTER',
}

export type UpdateHistoryPayload = {
  page: number;
  productCode: Product['code'];
};

const canRequestSkeletonLoaders = (
  { currentPage }: ProductsState,
  { page }: qs.ParsedUrlQueryInput = {},
) => {
  const isInitialPageLoad = currentPage === 0;
  const isLoadingNewPage = page != null;

  return isInitialPageLoad || !isLoadingNewPage;
};

const getQueryForHistoryState = (page: number) => {
  // No need to do anything else if page not present in the browser, because it's always implicity page=1.
  return `${location.search.replace(/page=\d+/, `page=${page}`)}`;
};

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions,
  { rejectValue: ProductsAPIHandledRejection }
>(
  'products/requestProducts',
  async (
    { query, cookie, isHealthcheckRequest, triggerName },
    { getState, dispatch, rejectWithValue },
  ) => {
    if (isHealthcheckRequest) {
      // Dynamically import json file to prevent it being included on the client. Healthcheck request only happens server side.
      const mockBrowseServiceProductsData = await import('./mocks/giftsForHerCategory.json');

      return {
        data: transformBrowseServiceProductsToReduxActionPayload(
          mockBrowseServiceProductsData as unknown as BrowseServiceProductsData,
        ),
      };
    }

    const state = getState() as ReduxApplicationState;
    const { deliveryZone, loop54UserId, featureFlags } = state.userConfiguration;
    const currencyCode = state.userConfiguration.currencyCode!;

    if (canRequestSkeletonLoaders(state.products, query)) {
      dispatch(addLoadingProducts(Number(query.page) || 1));
    }

    const commonGetDataArgs = {
      cookie,
      ...(currencyCode && { currencyCode }),
      ...(deliveryZone && { deliveryZone }),
      featureFlags,
    };
    let response;

    switch (selectPageType(state)) {
      case PageType.Search: {
        const { gaVisitorId } = state.userConfiguration;

        response = await getSearchListingsData({
          ...commonGetDataArgs,
          query,
          userId: gaVisitorId,
        });
        break;
      }
      case PageType.Category: {
        const { previewing, ...restOfQuery } = query;
        const { cognitoId, elevaateSessionId, gaVisitorId } = state.userConfiguration;
        const browseProviderFeatureFlag = selectBrowseProviderFeatureFlag(state);
        const googleRetailBrowseProvider = browseProviderFeatureFlag === BrowseProvider.GOOGLE;
        const userId = googleRetailBrowseProvider ? gaVisitorId : loop54UserId;

        response = await getCategoryListingsData({
          ...commonGetDataArgs,
          ...(previewing ? { previewing: true } : {}),
          elevaateUid: cognitoId,
          elevaateSessionId,
          query: restOfQuery,
          userId: userId,
          browseProvider: googleRetailBrowseProvider ? 'google' : 'loop54',
        });
        break;
      }
      case PageType.Partner: {
        const { shortcode, ...restOfQuery } = query;

        response = await getPartnerListingsData({
          ...commonGetDataArgs,
          query: restOfQuery,
          shortcode: shortcode ? String(shortcode) : selectPartnerShortcode(state),
          userId: loop54UserId,
        });
        break;
      }
      case PageType.AllProducts: {
        response = await getAllProductsListingsData({
          ...commonGetDataArgs,
          query,
          userId: loop54UserId,
        });
        break;
      }
      default:
        console.error('unknown page type');
    }

    if (response?.redirectUrl) {
      return { redirectUrl: response.redirectUrl };
    }

    if (!response?.data) {
      // TODO: don't pretend it's a client abort, when it's a problem with the browse-data-api response
      return rejectWithValue({ isClientAbort: true });
    }

    return {
      data: {
        ...transformBrowseServiceProductsToReduxActionPayload(response?.data),
        ...(query.term && { searchTerm: query.term as string }),
      },
      triggerName,
    } as RequestProductsActionPayload;
  },
);

const requestProductsIsRejected = isRejected(requestProducts);

export const refetchProducts = createAsyncThunk(
  'products/refetchProducts',
  async (_payload, { getState, dispatch }) => {
    const state = getState() as ReduxApplicationState;

    const queryParams = selectRouterQueryParameters(state);
    const path = selectRouterPathname(state);

    const productsQuery = {
      ...queryParams,
      path,
    };

    await dispatch(
      requestProducts({
        query: productsQuery,
      }),
    );
  },
);

export const updateFilters = createAsyncThunk(
  'products/updateFilters',
  async (filterType: UpdateFiltersActionType, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as ReduxApplicationState;
    const {
      router: {
        location: { pathname },
      },
      filter: {
        collections: { lastUpdatedFilter },
      },
    } = state;

    const queryObject = selectRequestOptionsAsQueryObject(state);

    const query = qs.stringify(queryObject);

    dispatch(
      replace(
        {
          pathname: selectRouterPathname(state),
          query,
        },
        query ? `${pathname}?${query}` : pathname,
        { shallow: true, scroll: false },
      ),
    );

    if (lastUpdatedFilter) {
      const { filterUid, filterOptionValue, type } = lastUpdatedFilter;

      queryObject.updatedFilter = filterUid;
      queryObject.updatedFilterOption = filterOptionValue;
      queryObject.updatedFilterAction = type === 'SELECT' ? 'selected' : 'deselected';
    }

    const action = await dispatch(
      requestProducts({
        query: {
          ...queryObject,
          path: pathname,
        },
      }),
    );

    if (requestProductsIsRejected(action)) {
      return rejectWithValue('There was a problem loading products');
    }

    const productContainerOffsetTop = document.getElementById(PRODUCT_CONTAINER_WRAPPER)?.offsetTop;
    window.scrollTo({ top: productContainerOffsetTop, behavior: 'auto' });

    return filterType;
  },
);

export const loadPageOfProducts = createAsyncThunk(
  'products/loadPageOfProducts',
  async (pageNumber: number, { getState, dispatch }) => {
    const state = getState() as ReduxApplicationState;
    const {
      router: {
        location: { pathname },
      },
    } = state;

    const queryObject = {
      ...selectRequestOptionsAsQueryObject(state),
      page: pageNumber,
    };

    if (pageNumber > selectCurrentPage(state)) {
      const query = qs.stringify(queryObject);

      dispatch(
        replace(
          {
            pathname: selectRouterPathname(state),
            query,
          },
          `${pathname}?${query}`,
          { shallow: true, scroll: false },
        ),
      );
    }

    return await dispatch(
      requestProducts({
        query: {
          ...queryObject,
          path: pathname,
        },
        replaceProducts: false,
        triggerName:
          pageNumber > selectCurrentPage(state)
            ? TriggerName.LOAD_MORE_PRODUCTS
            : TriggerName.LOAD_PREVIOUS_PRODUCTS,
      }),
    ).then(unwrapResult);
  },
);

// Register elevaate product impressions for sponsored products rendered in the page
export const elevaateProductImpressions = createAsyncThunk(
  'products/elevaateProductImpressions',
  async ({ products }: { products: Product[] }) => {
    let elevaatePageImpressionId = '';

    const sponsoredProductsQueryString = products.reduce((acc, item) => {
      if (!item.sponsoredProductData) {
        return acc;
      }

      const { sponsoredProductData } = item;
      const sponsoredProductQueryString = encodeURIComponent(
        `${sponsoredProductData['data-elevaate-aid']}|${sponsoredProductData['data-elevaate-pid']}`,
      );

      if (acc === '') {
        elevaatePageImpressionId = sponsoredProductData['data-elevaate-piid'];

        return acc.concat(sponsoredProductQueryString);
      }

      return acc.concat(`,${sponsoredProductQueryString}`);
    }, '');

    if (sponsoredProductsQueryString.length > 0) {
      const elevaateImpressionsUrlFragment = `${elevaatePageImpressionId}/renderPageCharge.json?prms=${sponsoredProductsQueryString}`;
      const elevaateImpressionsUrl = `${publicRuntimeConfig.endpoints.elevaateImpressions}/${publicRuntimeConfig.ELEVAATE_API_KEY}/${elevaateImpressionsUrlFragment}`;

      await fetch(elevaateImpressionsUrl)
        .then(handleFetchResponse)
        .then((data) => {
          console.log(`Registered impressions for: ${elevaateImpressionsUrlFragment}.`, data);
        });
    }
  },
);

export const updatePageHistory = createAsyncThunk(
  'products/updatePageHistory',
  async ({ page, productCode }: UpdateHistoryPayload) => {
    const query = getQueryForHistoryState(page);
    history.replaceState(null, '', `${location.pathname}${query}`);
    // If not manual then page jumps up and down with a race condition because browser tries to go back in a wrong place.
    history.scrollRestoration = 'manual';
    pageHistory?.set('lastViewedProduct', { productCode });
  },
);

interface RequestFeaturedProductArguments {
  code: number;
}

export interface RequestFeaturedProductAPIResponse {
  product: Product;
}

export const requestFeaturedProduct = createAsyncThunk<Product, RequestFeaturedProductArguments>(
  'products/featuredProduct',
  async ({ code }) => {
    const apiResponse = await requestWithTimeout({
      apiEndpoint: publicRuntimeConfig.endpoints.catalogueMesh,
      apiPath: `v1/product-details?code=${code}`,
    });

    const { product }: RequestFeaturedProductAPIResponse = await handleFetchResponse(
      apiResponse as Response,
    );

    return product;
  },
);
